import { useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { api } from "@/lib/api";

const DEFAULT_PRODUCT = { id: "anime-physique-program", name: "Anime Physique Program", price: "19.99" };

// A service is § 356 Abs. 4 BGB — the withdrawal right expires only on FULL
// performance, so across a 4-week block it does not vanish at checkout the way
// it does for digital content (Abs. 5). Wrong wording = no Wertersatz claim.
const WAIVER_TEXT = {
  file: (
    <>
      Ich stimme ausdrücklich zu, dass mit der Ausführung des Vertrags (Bereitstellung des
      Downloads) vor Ablauf der Widerrufsfrist begonnen wird. Mir ist bekannt, dass mein
      Widerrufsrecht mit Beginn der Ausführung erlischt (§ 356 Abs. 5 BGB).
    </>
  ),
  service: (
    <>
      Ich verlange ausdrücklich, dass mit der Coaching-Leistung vor Ablauf der Widerrufsfrist
      begonnen wird. Mir ist bekannt, dass mein Widerrufsrecht mit vollständiger Erbringung der
      Leistung erlischt (§ 356 Abs. 4 BGB) und dass ich bei einem Widerruf während der Laufzeit
      anteiligen Wertersatz für die bereits erbrachte Leistung schulde (§ 357 Abs. 8 BGB).
    </>
  ),
};

export const CheckoutDialog = ({ open, onOpenChange, paypalEnabled, product }) => {
  const p = product || DEFAULT_PRODUCT;
  const isService = p.fulfillment === "service";
  const [email, setEmail] = useState("");
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [loading, setLoading] = useState(null);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const pay = async (provider) => {
    if (!validEmail) {
      toast.error("Enter a valid email — that's where your VIP code goes.");
      return;
    }
    if (!waiverAccepted) {
      toast.error("Bitte bestätige die Checkbox zum Widerrufsrecht.");
      return;
    }
    setLoading(provider);
    try {
      const endpoint = provider === "stripe" ? "/checkout/session" : "/paypal/create-order";
      const { data } = await api.post(endpoint, {
        product_id: p.id,
        origin_url: window.location.origin,
        email,
        withdrawal_waiver: waiverAccepted,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-2 border-[#39FF14] rounded-none max-w-md" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-anton text-2xl uppercase neon-text tracking-wide">Secure Your Copy</DialogTitle>
          <DialogDescription className="text-[#A1A1AA] font-jet text-xs">
            {p.name} — €{p.price} one-time.{" "}
            {isService
              ? "Your intake link, VIP code & Discord invite go to this email."
              : "Your confirmation, VIP code & Discord invite go to this email."}
          </DialogDescription>
        </DialogHeader>
        <input
          data-testid="checkout-email-input"
          type="email"
          className="input-ady"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <label className="flex items-start gap-3 mt-1 cursor-pointer select-none">
          <input
            data-testid="checkout-waiver-checkbox"
            type="checkbox"
            checked={waiverAccepted}
            onChange={(e) => setWaiverAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#39FF14] cursor-pointer"
          />
          <span className="font-jet text-[10px] text-[#A1A1AA] leading-relaxed">
            {WAIVER_TEXT[isService ? "service" : "file"]} Es gelten die{" "}
            <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-[#39FF14]">AGB</a> und die{" "}
            <a href="/widerruf" target="_blank" rel="noopener noreferrer" className="text-[#39FF14]">Widerrufsbelehrung</a>.
          </span>
        </label>
        <div className="flex flex-col gap-4 mt-2">
          <button
            data-testid="checkout-stripe-btn"
            className="btn-brutal w-full"
            disabled={loading !== null}
            onClick={() => pay("stripe")}
          >
            {loading === "stripe" ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />}
            Pay with Card
          </button>
          <button
            data-testid="checkout-paypal-btn"
            className="btn-brutal btn-brutal-green w-full"
            disabled={loading !== null || !paypalEnabled}
            onClick={() => pay("paypal")}
            title={paypalEnabled ? "" : "PayPal coming online soon"}
          >
            {loading === "paypal" ? <Loader2 className="animate-spin" size={18} /> : null}
            {paypalEnabled ? "Pay with PayPal" : "PayPal — Available Soon"}
          </button>
        </div>
        <p className="font-jet text-[10px] text-[#52525B] text-center mt-2">
          {isService
            ? "Intake & Discord access after payment • Secure checkout"
            : "Instant download after payment • Secure checkout"}
        </p>
      </DialogContent>
    </Dialog>
  );
};
